// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { Container } from './container';
import { Component } from './component';
import { Cookie } from './cookie';
import { BrowserFeatures } from './browser-features';
import { ProgressDialog } from './progress-dialog';
import { Popup } from './popup';
import { render, prepareUrl, createElement, getComponent, createComponent } from './common';
import { redirect } from './form-redirect';
import { Observer } from './observer';

/**
 * @class Jsw.Task.ProgressBar
 * @extends Jsw.Container
 */
export const ProgressBar = Class.create(Container, {
    _initConfiguration($super, config) {
        $super(config);

        this.pe = null;
        this._contentAreaId = `${this._id}-content-area`;
        this._preparingItems = [];
        this._preparingCounter = 0;
        this._deletedIds = [];
        if (!this._getConfigParam('items')) {
            this.update();
        } else {
            this._itemsReady = true;
        }
    },

    _initComponentElement($super) {
        $super();

        const isAsyncProgressBarCollapsed = Cookie.get('isAsyncProgressBarCollapsed');

        this._updateComponentElement(
            `<div id="asyncProgressBar" class="async-progress-bar ${'true' === isAsyncProgressBarCollapsed ? 'async-progress-bar-collapsed' : ''} js-scrollbar-spacer"> ` +
                '<div class="async-progress-bar-wrap">' +
                    '<div id="asyncProgressBarTop" class="async-progress-bar-top">' +
                        '<div class="async-progress-bar-control">' +
                            `<a id="asyncProgressBarControlHide" href="#" class="async-progress-bar-control-hide">${this.lmsg('progressBarHide')}</a>` +
                            `<a id="asyncProgressBarControlShow" href="#" class="async-progress-bar-control-show">${this.lmsg('progressBarShow')}</a>` +
                        '</div>' +
                        '<div class="async-progress-bar-title">' +
                            '<span id="asyncProgressBarTitleTasks" class="async-progress-bar-title-tasks hidden"></span>' +
                            '<span id="asyncProgressBarTitleTasksError" class="async-progress-bar-title-tasks-error hidden"></span>' +
                            '<span id="asyncProgressBarTitleTasksComplete" class="async-progress-bar-title-tasks-complete hidden"></span>' +
                            '<span id="asyncProgressBarTitleTasksWarning" class="async-progress-bar-title-tasks-warning hidden"></span>' +
                            `<a id="asyncProgressBarHideCompletedTasks" class="async-progress-bar-title-tasks-hide hidden" href="#">${this.lmsg('hideCompletedTasks')}</a>` +
                        '</div>' +
                    '</div>' +
                    '<div class="async-progress-bar-body">' +
                        `<ul class="async-progress-bar-list" id="${this._contentAreaId}">` +
                        '</ul>' +
                    '</div>' +
                '</div>' +
            '</div>'
        );
    },

    addPreparingItem(title) {
        const id = `preparing-${this._preparingCounter}`;
        const item = new ProgressBar.Item({
            errors: [],
            id,
            progressTitle: title,
            status: 'flying',
        });
        this._preparingItems.unshift(item);
        this._preparingCounter++;
        this._items.unshift(item);
        return id;
    },

    removePreparingItem(id) {
        this._preparingItems = this._preparingItems.filter(function (item) {
            return item.getId() != id;
        });
        this._deletedIds.push(id);
    },

    toggle() {
        const element = $('asyncProgressBar');
        element.toggleClassName('async-progress-bar-collapsed');

        Cookie.setPermanent(
            'isAsyncProgressBarCollapsed',
            element.hasClassName('async-progress-bar-collapsed'),
            '/'
        );

        this._updateTitle();
    },

    update() {
        return new Ajax.Request(this._getConfigParam('progressUrl'), {
            method: 'get',
            onSuccess: function (response) {
                this.removeItemsByIds(this._deletedIds);
                this._deletedIds = [];
                const data = response.responseText.evalJSON();
                this.checkPreviousStatus(data.items);
                this.setItems(data.items);
                this._updateProgressDialog();
                this._itemsReady = true;
                this._renderItems();
            }.bind(this),
            on400: response => {
                // prevents page reload
                throw new Error(`${response.status} (${response.statusText})`);
            },
        });
    },

    removeItemsByIds(ids) {
        if (!ids.length) {
            return;
        }
        const items = this.getItems();
        for (let i = 0; i < ids.length; i++) {
            for (let j = 0; j < items.length; j++) {
                if (items[j].getId() === ids[i]) {
                    items[j].getRenderTarget().remove();
                    items.splice(j, 1);
                    break;
                }
            }
        }
        this.setItems(items);
        this._renderItems();
    },

    checkPreviousStatus(newItems) {
        newItems.each(newItemData => {
            const item = this.getItem(newItemData.id);
            if (item) {
                newItemData.isRefreshLinkEnabled = item._isRefreshLinkEnabled;
                if (newItemData.status !== item._status) {
                    this.onItemStatusChange(newItemData);
                }
                if (ProgressBar.Item.STATUS_STARTED !== newItemData.status && item.isStarted()) {
                    newItemData.isRefreshLinkEnabled = true;
                }
            } else {
                this.onItemStatusChange(newItemData);
            }
        });
    },

    onItemStatusChange(newItemData) {
        const isComplete = status => ['done', 'error', 'canceled'].indexOf(status) !== -1;
        if (isComplete(newItemData.status)) {
            Observer.notify(newItemData, 'plesk:taskComplete');
        }
    },

    setItems(items) {
        this._initItems(this._preparingItems.concat(items));
    },

    removeCompletedTask() {
        const ids = this._items.filter(function (item) {
            return !item.isStarted();
        }).map(function (item) {
            return item.getId();
        });

        if (ids.length > 0) {
            new Ajax.Request(this._getConfigParam('removeUrl'), {
                method: 'post',
                parameters: { 'ids[]': ids },
                onSuccess: function () {
                    this.removeItemsByIds(ids);
                }.bind(this),
            });
        }
    },

    fly(beginOffset, taskName, action) {
        if (!BrowserFeatures.transition) {
            action();
            return;
        }

        const item = new Element('div', { class: 'async-progress-bar-floating-item' });
        item.insert(taskName);
        item.setStyle({
            top: `${beginOffset.top}px`,
            left: `${beginOffset.left}px`,
        });
        $$('body')[0].insert({ bottom: item });
        const progressBarComponent = this;
        const progressBarItemId = progressBarComponent.addPreparingItem(taskName);
        progressBarComponent._renderItems();
        const progressBarPreparingItem = $$('.async-progress-bar-preparing-item-begin').first();
        if ($('asyncProgressBar').hasClassName('async-progress-bar-collapsed')) {
            this.toggle();
        }
        setTimeout(function () {
            const progressBarTotalHeight = progressBarPreparingItem.getHeight();
            progressBarPreparingItem.addClassName('async-progress-bar-preparing-item');
            setTimeout(function () {
                progressBarPreparingItem.addClassName('async-progress-bar-preparing-item-end');
                const endOffset = $$('ul.async-progress-bar-list')[0].cumulativeOffset();
                const endScrollOffset = $$('ul.async-progress-bar-list')[0].cumulativeScrollOffset();
                const progressBar = progressBarPreparingItem.firstDescendant();
                const height = progressBar.measure('height');
                const width = progressBar.measure('width');
                item.setStyle({
                    top: `${endOffset.top + endScrollOffset.top - progressBarTotalHeight}px`,
                    left: `${endOffset.left}px`,
                    height: `${height}px`,
                    width: `${width}px`,
                });
                item.addClassName('async-progress-bar-floating-item-end');
                const transitionEvents = $w('webkitTransitionEnd transitionend msTransitionEnd oTransitionEnd');
                transitionEvents.each(function (ev) {
                    item.observe(ev, function () {
                        transitionEvents.each(function (ev2) {
                            item.stopObserving(ev2);
                        });
                        item.remove();
                        const progressBarItem = getComponent(progressBarItemId);
                        progressBarItem.setStatus(progressBarItem.STATUS_PREPARING);
                        const progressBar = progressBarPreparingItem.firstDescendant();
                        progressBar.setStyle({ visibility: 'visible' });
                        action();
                    });
                });
            }, 0);
        }, 0);

        return progressBarItemId;
    },

    progressDialog(task, params) {
        const returnUrl = task && task.returnUrl;
        params = Object.extend({
            progressBarItem: task instanceof ProgressBar.Item ? task : createComponent(task),
            locale: this.getLocale(),
            onComplete: () => {
                if (this._progressDialog) {
                    const { progressBarItem } = this._progressDialog;
                    const redirectUrl = progressBarItem._getConfigParam('redirect', returnUrl);
                    if (redirectUrl) {
                        progressBarItem.remove().then(() => redirect(redirectUrl));
                    }
                }
            },
            onHide: () => {
                if (this._progressDialog) {
                    const { progressBarItem } = this._progressDialog;
                    const redirectUrl = progressBarItem._getConfigParam('redirect', returnUrl);
                    if (progressBarItem.isComplete()) {
                        progressBarItem.remove().then(() => redirectUrl && redirect(redirectUrl));
                    } else if (returnUrl) {
                        redirect(returnUrl);
                    }
                }
                delete this._progressDialog;
                this.show();
            },
        }, params || {});
        this._progressDialog = new ProgressDialog(params);

        this.update();
        this.hide();
    },

    _updateProgressDialog() {
        if (this._progressDialog) {
            const itemId = this._progressDialog.progressBarItem.getId();
            this._progressDialog.updateItem(this.getItem(itemId));
        }
    },

    _renderItems($super) {
        $super();

        this._updateTitle();
        if (this._items.length) {
            this._componentElement.removeClassName('hidden');
            this._setPeriodicalExecutor();
        } else {
            this._componentElement.addClassName('hidden');
        }
    },

    _setPeriodicalExecutor() {
        const context = this;
        if (null === this.pe && this._hasStartedTasks()) {
            this.pe = new PeriodicalExecuter(function () {
                context.update();
                if (!context._hasStartedTasks()) {
                    context.pe.stop();
                    context.pe = null;
                }
            }, 5);
        }
    },

    _hasStartedTasks() {
        return this._items.any(function (item) {
            return item.isStarted();
        });
    },

    _updateTitle() {
        let countRunning = 0;
        let countCompleteError = 0;
        let countCompleteSuccessfully = 0;
        let countCompleteWarning = 0;

        this._items.forEach(item => {
            if (item.isCompleteSuccessfully()) {
                countCompleteSuccessfully++;
            } else if (item.isCompleteWithWarning()) {
                countCompleteWarning++;
            } else if (item.isCompleteWithError()) {
                countCompleteError++;
            } else {
                countRunning++;
            }
        });

        const taskRunningElement = $('asyncProgressBarTitleTasks');
        if (countRunning > 0) {
            if (countRunning === this._items.length) {
                taskRunningElement.update(this.lmsg('taskInProgress', { count: countRunning }));
            } else {
                taskRunningElement.update(countRunning);
            }
            $('asyncProgressBar').removeClassName('async-progress-bar-complete');
            taskRunningElement.removeClassName('hidden');
        } else {
            $('asyncProgressBar').addClassName('async-progress-bar-complete');
            taskRunningElement.addClassName('hidden');
        }
        if (countCompleteSuccessfully > 0 || countCompleteWarning > 0 || countCompleteError > 0) {
            $('asyncProgressBarHideCompletedTasks').removeClassName('hidden');
        } else {
            $('asyncProgressBarHideCompletedTasks').addClassName('hidden');
        }

        this._updateTaskTitleElement('asyncProgressBarTitleTasksError', countCompleteError);
        this._updateTaskTitleElement('asyncProgressBarTitleTasksWarning', countCompleteWarning);
        this._updateTaskTitleElement('asyncProgressBarTitleTasksComplete',
            $('asyncProgressBar').hasClassName('async-progress-bar-collapsed') && countCompleteSuccessfully === this._items.length
                ? this.lmsg('allTasksCompleted', { num: countCompleteSuccessfully })
                : countCompleteSuccessfully
        );
    },

    _updateTaskTitleElement(id, title) {
        if (!this._itemsReady) {
            return;
        }
        const element = $(id);

        if (element._oldValue !== undefined && title > element._oldValue) {
            element.update(`<span>${title}</span>`);
        } else {
            element.update(title);
        }
        element.toggleClassName('hidden', title === 0);
        element._oldValue = title;
    },

    _renderItem(item) {
        const renderTargetId = `${this._id}-item-${item.getId()}`;
        if (!$(renderTargetId)) {
            $(this._contentAreaId).insert({
                top: `<li id="${renderTargetId}" class="async-progress-bar-item"></li>`,
            });
        }
        item.setLocale(this.getLocale());
        item.setProgressBarElement(this);
        render($(renderTargetId), item);
    },

    _addEvents($super) {
        $super();

        const context = this;
        $('asyncProgressBarTop').observe('click', function (e) {
            e.preventDefault();
            context.toggle();
        });
        $('asyncProgressBarHideCompletedTasks').observe('click', function (e) {
            e.preventDefault();
            context.removeCompletedTask();
        });
    },
});

/**
 * @class Jsw.Task.ProgressBar.Item
 * @extends Jsw.Component
 */
ProgressBar.Item = Class.create(Component, {
    STATUS_DONE: 'done',
    STATUS_ERROR: 'error',
    STATUS_STARTED: 'started',
    STATUS_RUNNING: 'running',
    STATUS_NOT_STARTED: 'not_started',
    STATUS_PREPARING: 'preparing',
    STATUS_FLYING: 'flying',
    STATUS_CANCELED: 'canceled',

    _initConfiguration($super, config) {
        $super(config);

        this._id = this._getConfigParam('id', '');
        this._status = this._getConfigParam('status', '');
        this._errors = this._getConfigParam('errors', []);
        this._output = this._getConfigParam('output', []);
        this._isRefreshLinkEnabled = this._getConfigParam('isRefreshLinkEnabled', false)
            && this._getConfigParam('isRefreshAllowed', true);
        this._progressValue = this._getConfigParam('progressValue', 0);
        this._canCancel = this._getConfigParam('canCancel', true);
        this._referrer = this._getConfigParam('referrer', '');
        this._progressBarElement = {};
    },

    render() {
        const ce = createElement;
        let progressBar = [];
        const element = this.getRenderTarget();

        if (this.isCompleteSuccessfully()) {
            element.addClassName('async-progress-bar-item-complete');
            progressBar = ce('.async-progress-bar-item-msg', [
                this.getCloseButton(),
                ce('.async-progress-bar-item-msg-body', [
                    this.getProgressTitle(),
                    this.hasOutput() ? ce('.output-details', ce('ul', this.getOutputString())) : '',
                    this.getRefreshLink(),
                ]),
            ]);
        } else if (this.isCompleteWithWarning() || this.isCompleteWithError()) {
            element.addClassName(this.isCompleteWithWarning() ? 'async-progress-bar-item-warning' : 'async-progress-bar-item-error');
            progressBar = ce('.async-progress-bar-item-msg', [
                this.getCloseButton(),
                ce('.async-progress-bar-item-msg-body', [
                    this.getProgressTitle(),
                    this.getErrorsString(),
                    this.getRefreshLink(),
                ]),
            ]);
        } else if (this.isPreparing() || this.isFlying() || this.isProgressUndefined()) {
            if (!this.isProgressUndefined()) {
                element.addClassName('async-progress-bar-preparing-item-begin');
            }
            progressBar = [
                ce('.async-progress-bar-item-heading', { style: this.isFlying() ? 'visibility: hidden' : '' }, [
                    ce('.async-progress-bar-item-title', this.getProgressTitle()),
                ]),
                ce('.progress.progress-sm', ce('.progress-bar.progress-bar-striped.active', { style: 'width: 100%' })),
            ];
            const link = this.getProgressDialogLink();
            if (link) {
                progressBar.push(ce('.async-progress-bar-item-footer', [link, '&nbsp;']));
            }
        } else {
            progressBar = [
                ce('.async-progress-bar-item-heading', [
                    ce('.async-progress-bar-item-control', this.getCancelButton()),
                    ce('.async-progress-bar-item-title', this.getProgressTitle()),
                ]),
                ce('.progress.progress-sm', ce('.progress-bar', { style: `width: ${this.getProgressValue()}%` })),
                ce('.async-progress-bar-item-footer', [
                    this.getProgressDialogLink(),
                    this.lmsg('percentCompleted', { percent: this.getProgressValue() }),
                ]),
            ];
        }
        render(element, ce('.async-progress-bar-item-wrap', progressBar), 'inner');

        this._addEvents();
        this._addTooltips();
    },

    getCloseButton() {
        const ce = createElement;
        return ce('a', {
            href: '#',
            onclick: function (event) {
                event.preventDefault();
                this.remove();
            }.bind(this),
        }, ce('span.close'));
    },

    getSteps() {
        return this._getConfigParam('steps', {});
    },

    setProgressBarElement(element) {
        this._progressBarElement = element;
    },

    getProgressBarElement() {
        return this._progressBarElement;
    },

    getProgressTitle() {
        return this._getConfigParam('progressTitleHtml', this._getConfigParam('progressTitle', '').escapeHTML());
    },

    getProgressValue() {
        return this._progressValue;
    },

    getStatus() {
        return this._status;
    },

    setStatus(status) {
        this._status = status;
    },

    getProgressDialogLink() {
        if (!Object.keys(this.getSteps()).length) {
            return;
        }
        const ce = createElement;
        return ce('.async-progress-bar-item-control', ce('a', {
            href: '#',
            onclick: function (event) {
                event.preventDefault();
                this.getProgressBarElement().progressDialog(this);
            }.bind(this),
        }, this.lmsg('progressDialogLink')));
    },

    getRefreshLink() {
        const ce = createElement;
        const _redirect = this._getConfigParam('redirect');
        if (_redirect) {
            return ce('', ce('a', {
                href: '#',
                onclick(event) {
                    event.preventDefault();
                    redirect(_redirect);
                },
            }, _redirect.title ? _redirect.title.escapeHTML() : this.lmsg('refresh')));
        }

        if (this._isRefreshLinkEnabled && window.location.pathname === this._referrer) {
            return ce('', ce('a', {
                href: '#',
                onclick(event) {
                    event.preventDefault();
                    redirect(prepareUrl(window.location.pathname));
                },
            }, this.lmsg('refresh')));
        }
        return '';
    },

    hasErrors() {
        return this._errors.length > 0;
    },

    getErrors() {
        return this._errors;
    },

    getErrorsString() {
        const hideErrors = this._getConfigParam('hideErrors', false);
        if (hideErrors || !this.hasErrors()) {
            return null;
        }
        const ce = createElement;
        return ce('.error-details', ce('ul',
            this.getErrors().map(function (error) {
                return ce('li', String(error).escapeHTML());
            })
        ));
    },

    hasOutput() {
        return this._output.length > 0;
    },

    getOutputString() {
        return this._output.map(function (line) {
            return `<li>${String(line).escapeHTML()}</li>`;
        }).join('');
    },

    getCancelButton() {
        // PPP-13558
        return '';
        // if (!this._canCancel) {
        //     return '';
        // }
        // const ce = createElement;
        // return ce('a', {
        //     href: '#',
        //     onclick: function (event) {
        //         event.preventDefault();
        //         this.remove();
        //     }.bind(this),
        // }, this.lmsg('cancel'));
    },

    isCompleteSuccessfully() {
        return this.STATUS_DONE === this._status && !this.hasErrors();
    },

    isComplete() {
        return [this.STATUS_DONE, this.STATUS_ERROR, this.STATUS_CANCELED].indexOf(this._status) !== -1;
    },

    isCompleteWithWarning() {
        return this.STATUS_DONE === this._status && this.hasErrors();
    },

    isStarted() {
        return this.STATUS_STARTED === this._status || this.STATUS_NOT_STARTED === this._status;
    },

    isPreparing() {
        return this.STATUS_PREPARING === this._status;
    },

    isFlying() {
        return this.STATUS_FLYING === this._status;
    },

    isProgressUndefined() {
        return this._progressValue === -1;
    },

    isCompleteWithError() {
        return this.STATUS_ERROR === this._status;
    },

    remove() {
        return new Promise(function (resolve) {
            new Ajax.Request(this.getProgressBarElement()._getConfigParam('removeUrl'), {
                method: 'post',
                parameters: { 'ids[]': [this.getId()] },
                onSuccess: function () {
                    this.getProgressBarElement().removeItemsByIds([this.getId()]);
                    resolve();
                }.bind(this),
            });
        }.bind(this));
    },
});

/**
 * @class Jsw.Task.ProgressBar.OutputPopup
 * @extends Jsw.Popup
 */
ProgressBar.OutputPopup = Class.create(Popup, {
    initialize($super, event, link) {
        Event.extend(event).preventDefault();

        const config = link.readAttribute('data-config').evalJSON();
        this.setLocale(config.locale);

        const output = link.readAttribute('data-output');
        this._content = '<div class="tree-box" style="height: 400px">' +
            `<p>${output.escapeHTML().replace(/\n/gm, '<br>')}</p>` +
            '</div>';

        $super(config);
    },

    _initConfiguration($super) {
        $super({
            title: this.lmsg('popupTitle'),
            content: this._content,
            buttons: [{
                title: this.lmsg('popupClose'),
                handler(event, popup) {
                    popup.hide();
                },
            }],
        });
    },
});



// WEBPACK FOOTER //
// ./jsw/task-progress-bar.js